/**
 * One stage's shared template in the bulk invite, editable — split out of BulkInviteModal.tsx at
 * its line ceiling. Also carries the stage's interview-link picker, which renders only when the
 * stage has more than one live link.
 */
import { Input } from '@finn-auto/design-system/atoms/input';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { ExternalLink, RotateCcw } from 'lucide-react';
import type { FirstInviteTemplate } from '../../services/firstInviteApi';
import { COLORS } from './format';
import { EmailLibraryLink } from './EmailLibraryLink';
import { FixInterviewLink } from './FixInterviewLink';
import { LinkPicker } from './LinkPicker';

/**
 * The shared template for one stage, editable.
 *
 * Tokens stay visible as `{candidateFirstName}` here — this is the template, not
 * one candidate's email, and the table below shows what they become. Editing
 * affects THIS batch only; the links point at the two places that change the
 * copy permanently, so a fix worth keeping does not live only in someone's
 * memory of one send.
 */
export function TemplateEditor({
  template,
  postingId,
  value,
  edited,
  onChange,
  onReset,
  ownEmail,
  onPickLink,
}: {
  template: FirstInviteTemplate;
  /** The role, so the interview link is reachable even when there is none yet. */
  postingId: string;
  value: { subject: string; body: string };
  edited: boolean;
  onChange: (next: { subject: string; body: string }) => void;
  onReset: () => void;
  /** The board owner, so their own link reads "(yours)" in the picker. */
  ownEmail?: string;
  /** Re-draft this stage's batch on another of its live links. */
  onPickLink: (linkId: string) => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{template.stageName}</span>
          <SourceChip template={template} />
          {edited && (
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
            >
              edited — this batch only
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {edited && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          )}
          {/*
            "Change it permanently" now lands ON the template rather than on the library's front
            door, and in a NEW TAB — this modal holds per-batch edits, and a same-tab hop to go read
            a template used to discard them. The interview-link case keeps its own destination: that
            copy lives on the link, not in the Library.
          */}
          {template.source === 'link' ? (
            <FixInterviewLink
              linkId={template.linkId}
              postingId={postingId}
              stageId={template.stageId}
              stageName={template.stageName}
              label="Change it permanently"
            />
          ) : template.templateKey ? (
            <EmailLibraryLink templateKey={template.templateKey} label="Change it permanently" />
          ) : (
            /*
              `source: 'default'` — the last-resort assembled copy, which carries no templateKey.
              EmailLibraryLink renders nothing without one, so deep-linking alone would have removed
              the link from the ONE case where the recruiter most needs the Library: there is no
              stage template yet, and authoring it is the fix.
            */
            <a
              href="/email-library"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              title="No template for this stage yet — author one in the Email Library."
            >
              Write a template for this stage <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {/*
            The booking link is a SEPARATE fix from the wording, and it is the one
            the review table below most often indicts ("{bookingLink} resolved to
            nothing"). Rendered for every stage whose copy is not the link's own —
            that branch already links there and a second copy would just be noise.
          */}
          {template.source !== 'link' && (
            <FixInterviewLink
              linkId={template.linkId}
              postingId={postingId}
              stageId={template.stageId}
              stageName={template.stageName}
            />
          )}
        </div>
      </div>

      {/* The stage's whole batch goes out on ONE link; nothing renders unless there are several. */}
      <div className="mb-3 flex">
        <LinkPicker
          options={template.linkOptions}
          value={template.linkId}
          ownEmail={ownEmail}
          onChange={onPickLink}
        />
      </div>

      <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor={`subject-${template.stageId}`}>
        Subject
      </label>
      <Input
        id={`subject-${template.stageId}`}
        value={value.subject}
        onChange={(e) => onChange({ ...value, subject: e.target.value })}
      />

      <label className="mb-1 mt-3 block text-xs font-medium text-gray-600" htmlFor={`body-${template.stageId}`}>
        Body — {'{tokens}'} are filled in per candidate
      </label>
      <Textarea
        id={`body-${template.stageId}`}
        rows={14}
        value={value.body}
        onChange={(e) => onChange({ ...value, body: e.target.value })}
        className="font-mono text-xs"
      />
      {template.tokens.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-500">
          Available here: {template.tokens.map((t) => `{${t}}`).join(', ')}
        </p>
      )}
    </div>
  );
}

/** Where this stage's copy came from — the recruiter needs to know what they are editing. */
function SourceChip({ template }: { template: FirstInviteTemplate }) {
  if (template.source === 'link') {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{ backgroundColor: '#DCF5E4', color: '#136C34' }}
      >
        custom wording for this link
      </span>
    );
  }
  if (template.source === 'library') {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={{ backgroundColor: '#DCF5E4', color: '#136C34' }}
        title="From the Email Library, chosen by stage"
      >
        {template.label || 'Email Library'}
      </span>
    );
  }
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
      title="No template exists for this stage — the generic assembly is being used. It is not written for a first round."
    >
      generic fallback
    </span>
  );
}
